<template>
  <v-form
    ref="stepContainerForm"
    v-model="isValid"
    :name="`step-container-${name}`"
    @submit.prevent="submit"
  >
    <slot />
    <div
      class="d-flex mt-4"
      :class="{'flex-column': hasSteps && $vuetify.breakpoint.smAndDown, 'justify-space-between': $vuetify.breakpoint.mdAndUp}"
    >
      <div
        v-if="!hideSecondaryActions"
        class="d-flex"
        :class="{ 'btn-mobile': isMobile }"
      >
        <v-tooltip
          v-if="hasSteps && !isDisabled"
          bottom
          :disabled="!isDirty"
        >
          <template v-slot:activator="{ on }">
            <div v-on="on">
              <v-btn
                :disabled="isDirty"
                class="my-4"
                @click="$emit('nextStep', -1)"
              >
                {{ $t('onboarding.stepContainer.back') }}
              </v-btn>
            </div>
          </template>
          <span>
            {{ $t('onboarding.stepContainer.saveChanges') }}
          </span>
        </v-tooltip>
        <v-btn
          v-if="!isDisabled"
          class="my-4 ml-6"
          @click="$router.go()"
        >
          {{ $t('onboarding.stepContainer.cancel') }}
        </v-btn>
      </div>
      <v-spacer v-else />
      <div
        class="d-flex"
        :class="{ 'btn-mobile': isMobile }"
      >
        <loading-button
          v-if="!isDisabled"
          color="primary"
          type="submit"
          :disabled="!isValid || !isDirty"
          :loading="isLoading"
          class="ma-md-4 my-4 mr-6"
        >
          {{ $t('onboarding.stepContainer.save') }}
        </loading-button>
        <v-tooltip
          v-if="hasSteps && !isDisabled"
          bottom
          :disabled="!isDirty"
        >
          <template v-slot:activator="{ on }">
            <div v-on="on">
              <v-btn
                color="primary"
                :disabled="isDirty"
                class="my-4"
                @click="$emit('nextStep', 1)"
              >
                {{ $t('onboarding.stepContainer.next') }}
              </v-btn>
            </div>
          </template>
          <span>
            {{ $t('onboarding.stepContainer.saveChanges') }}
          </span>
        </v-tooltip>
      </div>
    </div>
  </v-form>
</template>

<script>
import * as Sentry from '@sentry/vue'
import LoadingButton from '@/components/loading-button.vue'
import bus, { eventNames } from '@/lib/eventBus'

export default {
  components: { LoadingButton },
  props: {
    data: {
      type: Object,
      required: true
    },
    onSubmit: {
      type: Function,
      required: true
    },
    requiredData: {
      type: Function,
      required: true
    },
    hasChanges: {
      type: Function,
      default: (oldData, data) => JSON.stringify(oldData) !== JSON.stringify(data)
    },
    hasSteps: {
      type: Boolean,
      default: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    hideSecondaryActions: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      isComplete: false,
      isDirty: false,
      isValid: true,
      isLoading: false,
      missingData: [],
      oldData: JSON.parse(JSON.stringify(this.data))
    }
  },
  computed: {
    isMobile () {
      return this.$vuetify.breakpoint.mdAndDown
    }
  },
  watch: {
    isDirty (value) {
      this.$emit('dirty', value)
    },
    missingData (value) {
      this.$emit('missingData', value)
    },
    data: {
      deep: true,
      handler (data) {
        if (this.hasChanges(this.oldData, data)) {
          this.isDirty = true
          this.missingData = this.getMissingKeys(data)
          this.oldData = JSON.parse(JSON.stringify(data))
        }
      }
    }
  },
  created () {
    this.missingData = this.getMissingKeys(this.data)
  },
  mounted () {
    this.$refs.stepContainerForm.validate()
  },
  methods: {
    getMissingKeys (object) {
      return Object.entries(this.requiredData(object)).filter(([key, value]) => !value).map(([key]) => key)
    },
    async submit () {
      this.isLoading = true

      if (this.$refs.stepContainerForm.validate()) {
        try {
          await this.onSubmit(this.data)
          this.isDirty = false
          bus.$emit(eventNames.SHOW_SNACKBAR, { color: 'success', text: this.$t('onboarding.stepContainer.saved') })
        } catch (error) {
          Sentry.addBreadcrumb({
            category: 'onboarding',
            message: 'Save failed',
            data: {
              data: JSON.stringify(this.data),
              error
            }
          })
          Sentry.captureException(error)
          bus.$emit(eventNames.SHOW_SNACKBAR, { color: 'error', text: this.$t('onboarding.stepContainer.saveError') })
        } finally {
          this.isLoading = false
        }
      }
      this.isLoading = false
    }
  }
}
</script>

<style scoped>
.btn-mobile {
  justify-content: space-between;
}
</style>
